import React, { FC, SyntheticEvent, useEffect, useMemo } from 'react';
import TwLoadTooltipInfo, { TwTripTooltipInfoProps } from '../TwLoadTooltipInfo/TwLoadTooltipInfo';
import { ISearchTagsResponse } from '../../redux/api/loadTags/loadTags.model';
import useAuth from '../../providers/Auth/Auth.provider';
import DateUtils from '../../utils/dateUtils';
import TwTripTooltipContent from '../TwLoadTooltipInfo/TwTripTooltipContent';
import Typography from '@mui/material/Typography';
import { Trip, TRIP_STATUS } from '../../redux/api/trips/trips.model';
import TwTripTooltipContentById from '../TwLoadTooltipInfo/TwTripTooltipContentById';

const TripsIds: FC<{
  trip?: Partial<Trip>;
  tripId?: string;
  tripOrder: number;
  tripsLength: number;
  tags: ISearchTagsResponse[];
  orderReadableId?: number | string;
  tripReadableId: number | string;
  clientName?: string;
  onTripClick?: (e: SyntheticEvent) => void;
}> = ({
  trip,
  tripId,
  tripOrder,
  tripsLength,
  tags,
  orderReadableId,
  tripReadableId,
  clientName,
  onTripClick
}): JSX.Element => {
  const {
    getTenantSetting,
    data: { userData }
  } = useAuth();
  const enableLtl = userData?.tenant?.ltl_enabled || false;
  const use24HourClock = getTenantSetting('use-24-hour-clock', false);

  const pickup = trip?.stops ? trip.stops[0] : null;
  const delivery = trip?.stops ? trip.stops[trip.stops.length - 1] : null;

  const tripData: TwTripTooltipInfoProps | undefined = useMemo(() => {
    if (trip) {
      return {
        loadId: '',
        tripId: trip.id || '',
        tripReadableId: trip.readable_id || '',
        tags: tags,
        tripStatus: trip.status || TRIP_STATUS.DRAFT,
        clientName: clientName || '',
        pickupLocation: trip.stops ? trip.stops[0].location : null,
        deliveryLocation: trip.stops ? trip.stops[trip.stops.length - 1].location : null,
        stops: trip.stops ? trip.stops.length - 2 : 0,
        pickupDate:
          pickup && pickup.start_date
            ? DateUtils.getMediumDate(new Date(pickup.start_date)).split(',')[0]
            : '',
        pickupStartTime: pickup
          ? DateUtils.formatTimeForTooltip(use24HourClock, pickup.start_time, pickup.start_date)
          : '',
        pickupEndTime: pickup
          ? DateUtils.formatTimeForTooltip(use24HourClock, pickup.end_time, pickup.start_date)
          : '',
        deliveryDate:
          delivery && delivery.end_date
            ? DateUtils.getMediumDate(new Date(delivery?.end_date)).split(',')[0]
            : '',
        deliveryStartTime: delivery
          ? DateUtils.formatTimeForTooltip(use24HourClock, delivery.start_time, delivery.end_date)
          : '',
        deliveryEndTime: delivery
          ? DateUtils.formatTimeForTooltip(use24HourClock, delivery.end_time, delivery.end_date)
          : '',
        unassigned: !!(trip.drivers && trip.drivers.length === 0),
        loadReadableId: orderReadableId || '',
        pickupNumber:
          trip.stops && trip.stops.length > 0 ? trip.stops[0].stop_identifier || '' : '',
        deliveryNumber:
          trip.stops && trip.stops.length > 0
            ? trip.stops[trip.stops.length - 1].stop_identifier || ''
            : '',
        segmentsCount: tripsLength,
        order: trip.sort_order || 0,
        driverName:
          trip.drivers && trip.drivers.length > 0
            ? `${trip.drivers[0]?.first_name} ${trip.drivers[0].last_name}`
            : '',
        truckName: trip.truck?.name || '',
        trailerName: trip.trailer?.name || ''
      };
    } else {
      return undefined;
    }
  }, [trip]);

  const tripReadableIdToShow =
    !enableLtl && orderReadableId
      ? `${orderReadableId}-${tripOrder + 1}`
      : tripReadableId
      ? `${tripReadableId}`
      : '';

  return (
    <TwLoadTooltipInfo
      tooltipContent={
        !!tripData ? (
          <TwTripTooltipContent tripData={tripData} />
        ) : (
          <TwTripTooltipContentById
            tripId={tripId}
            orderInfo={{
              orderReadableId: orderReadableId || '',
              clientName: clientName,
              tripsLength: tripsLength,
              tags: tags
            }}
          />
        )
      }
    >
      <Typography
        id={tripReadableIdToShow}
        onClick={(e): void => onTripClick && onTripClick(e)}
        component={'span'}
        sx={{ color: '#3A4DE9', fontSize: '14px', cursor: 'pointer' }}
      >{`${tripReadableIdToShow}${tripOrder < tripsLength - 1 ? ', ' : ''}`}</Typography>
    </TwLoadTooltipInfo>
  );
};

export default TripsIds;
